import React, {useState, useEffect, useRef, useMemo, useCallback} from 'react'
import {ALL_COLUMNS, SENSOR_LIST_COLUMNS_STORAGE_KEY, DEFAULT_VISIBLE_COLUMNS} from './SensorListColumnConfig.js'
import {Card} from 'primereact/card'
import {Badge} from 'primereact/badge'
import {ProgressSpinner} from 'primereact/progressspinner'
import {Button} from 'primereact/button'
import {Chip} from 'primereact/chip'
import {Dropdown} from 'primereact/dropdown'
import AlertsPage from '../Alerts/AlertsPage'
import { _fetchEnginList, _getTagHistories } from '../../api/api'
import {OlangItem} from '../../../shared/Olang/user-interface/OlangItem/OlangItem'
import { useDispatch } from 'react-redux'
import { setEditEngine, setSelectedEngine, removeEngine } from '../../../Engin/slice/engin.slice.js'
import { EnginEditor } from '../../../Engin/EnginEditor/EnginEditor'
import { setAlertParams } from '../../../../store/slices/alert.slice'
import { Toast } from 'primereact/toast'
import { fetchValidator } from '../../../Inventory/slice/inventory.slice'
import { API_BASE_URL_IMAGE } from '../../../../api/config'
import { PageHeader } from '../../../shared/PageHeader/PageHeader'
import { DataTableComponent } from '@omniyat/reactcomponents'
import SensorDetail from './SensorDetail.jsx'
import { fetchSites, getSites } from '../../../Site/slice/site.slice.js'
import { useSelector } from 'react-redux'
import { SelectButton } from 'primereact/selectbutton'
import { Divider } from 'primereact/divider'

// CSS pour les en-têtes de groupe avec effet colspan
const groupHeaderStyles = `
  .sensor-list-table .group-header-row td:not(:first-child) {
    display: none !important;
  }
  .sensor-list-table .group-header-row td:first-child {
    position: relative;
  }
  .sensor-list-table .group-header-row td:first-child::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: -2000px;
    bottom: 0;
    background-color: #f8f9fa;
    z-index: -1;
  }
`
const viewOptions = [
  { label: 'Grille', value: 'grid', icon: 'pi pi-th-large' },
  { label: 'Liste', value: 'list', icon: 'pi pi-list' }
]
const SensorList = () => {
  // Local state - simplified like AlertList
  const [sensors, setSensors] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedPeriod, setSelectedPeriod] = useState(7) // Default: 7 days
  const [historicalData, setHistoricalData] = useState({})
  const toast = useRef(null)
  const [loadingHistories, setLoadingHistories] = useState(false)
  const [globalFilterValue, setGlobalFilterValue] = useState('')
  const [sidebarVisible, setSidebarVisible] = useState(false)
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_VISIBLE_COLUMNS)
  const [groupBy, setGroupBy] = useState(null)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [selectedSensorForEdit, setSelectedSensorForEdit] = useState(null)
  const [selectedSensorForDetail, setSelectedSensorForDetail] = useState(null)
  const [showDetail, setShowDetail] = useState(false)
  const [showAlerts, setShowAlerts] = useState(false)
  const [activeAlertsCount, setActiveAlertsCount] = useState(0)
  const [viewMode, setViewMode] = useState('list') // 'list' ou 'grid'
  const worksites = useSelector(getSites)
  const [selectedWorksite , setSelectedWorksite] = useState([])
  const dispatch = useDispatch()
  // Column configuration
  const allColumns = ALL_COLUMNS
  
  const dt = useRef(null)

  // Fonctions de gestion du localStorage pour les colonnes
  const saveColumnsToStorage = (columns) => {
    try {
      localStorage.setItem(SENSOR_LIST_COLUMNS_STORAGE_KEY, JSON.stringify(columns))
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des colonnes:', error)
    }
  }

  const loadColumnsFromStorage = () => {
    try {
      const saved = localStorage.getItem(SENSOR_LIST_COLUMNS_STORAGE_KEY)
      if (saved) {
        const parsedColumns = JSON.parse(saved)
        // Vérifier que les colonnes sauvegardées sont valides
        const validColumns = parsedColumns.filter(col => 
          ALL_COLUMNS.some(availableCol => availableCol.key === col)
        )
        return validColumns.length > 0 ? validColumns : DEFAULT_VISIBLE_COLUMNS
      }
    } catch (error) {
      console.error('Erreur lors du chargement des colonnes:', error)
    }
    return DEFAULT_VISIBLE_COLUMNS
  }

  const handleColumnChange = (newColumns) => {
    setSelectedColumns(newColumns)
    saveColumnsToStorage(newColumns)
  }

  // Period options for historical data
  const periodOptions = [
    { label: '3 jours', value: 3 },
    { label: '7 jours', value: 7 },
    { label: '14 jours', value: 14 },
    { label: '21 jours', value: 21 },
    { label: '30 jours', value: 30 },
    { label: '90 jours', value: 90 }
  ]

  const labelBody = (data) => {
    if (data.isGroupHeader) {
      return (
        <div className="flex align-items-center gap-2 font-bold text-primary" style={{ 
          padding: '0.75rem', 
          backgroundColor: '#f8f9fa',
          margin: '-0.5rem -1rem',
          borderRadius: '4px'
        }}>
          <i className="pi pi-folder" />
          <span>{data.groupName}</span>
          <Badge value={data.groupCount} className="ml-2" />
        </div>
      )
    }
    return data.label
  }

  const macBody = (data) => {
    if (data.isGroupHeader) {
      return <div style={{ visibility: 'hidden' }}></div>
    }
    return data.macAddr
  }

  const temperatureBodyTemplate = useCallback((rowData) => {
    if (rowData.isGroupHeader) return ''
    
    // Use the same key logic as in processHistoricalData (prioritize tagname for engins)
    const macAddr = rowData.tagname || rowData.macAddr || rowData.dmac || rowData.ident || rowData.code || rowData.label || rowData.name || rowData.mac || rowData.id
    const stats = historicalData[macAddr]?.temperatureStats
    
    if (stats && stats.count > 0) {
      return (
        <div className="flex align-items-center justify-content-between" style={{ padding: '4px' }}>
          {/* Moyenne à gauche */}
          <div className="text-red-600 font-bold" style={{ fontSize: '16px' }}>
            {stats.avg.toFixed(1)}°C
          </div>
          {/* Min/Max superposés à droite */}
          <div className="text-right text-red-500" style={{ fontSize: '10px', lineHeight: '1.1' }}>
            <div>max {stats.max.toFixed(1)}°C</div>
            <div>min {stats.min.toFixed(1)}°C</div>
          </div>
        </div>
      )
    }
    
    // Fallback to current value if no historical data
    const tempValue = rowData.temperature !== null ? `${rowData.temperature}°C` : '-'
    return (
      <div className="text-">
        <span className="text-red-600 font-bold" style={{ fontSize: '16px' }}>
          {tempValue}
        </span>
      </div>
    )
  }, [historicalData])

  const humidityBodyTemplate = useCallback((rowData) => {
    if (rowData.isGroupHeader) return ''
    
    // Use the same key logic as in processHistoricalData (prioritize tagname for engins)
    const macAddr = rowData.tagname || rowData.macAddr || rowData.dmac || rowData.ident || rowData.code || rowData.label || rowData.name || rowData.mac || rowData.id
    const stats = historicalData[macAddr]?.humidityStats
    
    if (stats && stats.count > 0) {
      return (
        <div className="flex align-items-center justify-content-between" style={{ padding: '4px' }}>
          {/* Moyenne à gauche */}
          <div className="text-blue-600 font-bold" style={{ fontSize: '16px' }}>
            {stats.avg.toFixed(1)}%
          </div>
          {/* Min/Max superposés à droite */}
          <div className="text-right text-blue-500" style={{ fontSize: '10px', lineHeight: '1.1' }}>
            <div>max {stats.max.toFixed(1)}%</div>
            <div>min {stats.min.toFixed(1)}%</div>
          </div>
        </div>
      )
    }
    
    // Fallback to current value if no historical data
    const humidityValue = rowData.humidity !== null ? `${rowData.humidity}%` : '-'
    return (
      <div className="text-cente">
        <span className="text-blue-600 font-bold" style={{ fontSize: '16px' }}>
          {humidityValue}
        </span>
      </div>
    )
  }, [historicalData])


  const columns = useMemo(()=>[
      {
          "field": "photo",
          "header": "Photo",
          body:(data) => data.isGroupHeader ? '' : photoBodyTemplate(data),
          "style": {
              "width": "80px",
              "textAlign": "center"
          }
      },
      {
          "field": "label",
          "header": "Nom du Capteur",
          body: labelBody,
          "sortable": true,
          "className": "font-semibold"
      },
      {
          "field": "macAddr",
          "header": "Adresse MAC",
          body: macBody,
          "sortable": true,
          "className": "font-mono text-sm text-gray-600"
      },
      {
          "field": "actif",
          "header": "Statut",
          body: (data) => data.isGroupHeader ? '' : statusBodyTemplate(data),
          "sortable": true,
          "className": "text-center",
          "style": {
              "width": "120px"
          }
      },
      {
          "field": "latestValues",
          "header": "Dernières Valeurs",
          body: (data) => data.isGroupHeader ? '' : latestValuesBodyTemplate(data),
          "style": {
              "width": "160px"
          }
      },
      {
          "field": "temperature",
          "header": "Température",
          body: (data) => data.isGroupHeader ? '' : temperatureBodyTemplate(data),
          "sortable": true,
          "style": {
              "width": "140px"
          }
      },
      {
          "field": "humidity",
          "header": "Humidité",
          body: (data) => data.isGroupHeader ? '' : humidityBodyTemplate(data) ,
          "sortable": true,
          "style": {
              "width": "140px"
          }
      },
      {
          "field": "battery",
          "header": "Batterie",
          body: (data) => data.isGroupHeader ? '' : batteryBodyTemplate(data),
          "sortable": true,
          "style": {
              "width": "120px"
          }
      },
      {
          "field": "lastSeen",
          "header": "Dernière activité",
          body: (data) => data.isGroupHeader ? '' : lastSeenBodyTemplate(data),
          "style": {
              "width": "150px"
          }
      }
  ], [humidityBodyTemplate , temperatureBodyTemplate])

  const actions = useMemo(()=>{
    return [
      {
        label: "Détail",
        icon: "pi pi-eye text-blue-600",
        command: (e) => {
           handleViewDetail(e.item.data)   
        }
      },
      {
        label: "Modifier",
        icon: "pi pi-pencil text-indigo-600",
        command: (e) => {
          handleEditSensor(e.item.data)
        }
      },
      {
        label: "Supprimer",
        icon: "pi pi-trash text-red-600",
        command: (e) => {
          handleDeleteSensor(e.item.data)
        }
      }
    ]
  }, [])
  

  // Load active alerts count
  const loadActiveAlertsCount = async () => {
    try {
      // Utiliser l'API pour récupérer toutes les alertes
      const { _getAllAlertHistories } = await import('../../api/api')
      const response = await _getAllAlertHistories({})
      
      if (response.success && response.result && Array.isArray(response.result)) {
        // Compter les alertes actives (isEnd === 0)
        const activeCount = response.result.filter(alert => alert.isEnd === 0 && alert.isRead == 0).length
        setActiveAlertsCount(activeCount)
      }
    } catch (error) {
      console.error('Erreur lors du chargement du nombre d\'alertes actives:', error)
    }
  }

  // Load sensors from API using engin/list endpoint
  const handleLoadSensors = async () => {
    setLoading(true)
    setError(null)
    console.log('🚀 Chargement des engins depuis l\'API...')
    
    try {
      // Use _fetchEnginList for engin/list endpoint
      //1, 1000, null, 'engin'
      let filter = {}

      if(selectedWorksite){
        console.log('selectedWorksite:', selectedWorksite)
        filter.LocationObject= selectedWorksite?.type || ''
        filter.LocationID= selectedWorksite?.id || 0
      }
      const response = await _fetchEnginList(filter) // Load all engins
      console.log('📡 Réponse API engins:', response)
      
      if (response.data && response.data.success && response.data.result) {
        const enginsData = Array.isArray(response.data.result) ? response.data.result : []
        console.log('✅ Engins chargés:', enginsData.length)
        console.log('📊 Premier engin exemple:', enginsData[0])
        setSensors(enginsData)
      } else {
        console.log('❌ Aucune donnée d\'engin reçue')
        setSensors([])
        setError('Aucun engin trouvé')
      }
      
    } catch (error) {
      console.error('🚨 Erreur lors du chargement des capteurs:', error)
      setSensors([])
      setError('Erreur lors du chargement des capteurs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(()=>{
    handleLoadSensors();
  },[selectedWorksite])

  // Load sensors on component mount
  useEffect(() => {
    handleLoadSensors()
    loadActiveAlertsCount()
    
    // Charger les colonnes depuis le localStorage
    const savedColumns = loadColumnsFromStorage()
    setSelectedColumns(savedColumns)
    dispatch(fetchSites())
  }, [])

  // Load historical data when sensors are loaded or period changes
  useEffect(() => {
    if (sensors.length > 0) {
      handleLoadHistoricalData()
    }
  }, [sensors, selectedPeriod])

  // Load historical data for all sensors - same logic as Historique.tsx
  const handleLoadHistoricalData = async () => {
    if (sensors.length === 0) return
    
    setLoadingHistories(true)
    console.log(`📊 Chargement des données historiques pour ${sensors.length} capteurs sur ${selectedPeriod} jours...`)
    
    try {
      // Calculate date range for the selected period
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(endDate.getDate() - selectedPeriod)
      
      // Format dates for API (YYYY-MM-DD HH:mm:ss)
      const fromDate = startDate.toISOString().slice(0, 19).replace('T', ' ')
      const toDate = endDate.toISOString().slice(0, 19).replace('T', ' ')
      
      console.log('📊 Période:', fromDate, 'à', toDate)
      
      // Extract tagnames and srcIds from engins
      const tags = sensors.map(engin => engin.tagname).filter(Boolean) // tagname pour tags
      const srcIds = sensors.map(engin => engin.uid || engin.id).filter(Boolean) // uid/id pour srcIds
      console.log('📊 Tags (tagnames) à récupérer:', tags)
      console.log('📊 SrcIds (uid/id) à récupérer:', srcIds)
      
      // Call API without group parameter to get raw data like in Historique.tsx
      const response = await _getTagHistories({
        tags: tags, // tagname des capteurs
        from: fromDate,
        to: toDate,
        srcObject: 'engin',
        srcIds: srcIds // uid/id des capteurs
      })
      
      console.log('📊 Réponse données historiques:', response)
      
      if (response.success && response.response) {
        // Process raw data like in Historique.tsx
        const processedData = processHistoricalData(response.response, tags)
        setHistoricalData(processedData)
        console.log('✅ Données historiques traitées:', Object.keys(processedData).length, 'capteurs')
      } else {
        console.log('❌ Aucune donnée historique reçue')
        setHistoricalData({})
      }
      
    } catch (error) {
      console.error('🚨 Erreur lors du chargement des données historiques:', error)
      setHistoricalData({})
    } finally {
      setLoadingHistories(false)
    }
  }

  // Process historical data with pre-calculated min/max/avg from API
  const processHistoricalData = (rawData, tags) => {
    if (!rawData || !Array.isArray(rawData)) return {}
    console.log('rawData:', rawData)
    console.log('📊 Traitement des données avec min/max pré-calculés:', rawData.length, 'éléments')
    
    // Group data by sensor MAC address and type
    const sensorData = {}
    
    rawData.forEach((item) => {
      const macAddr = item.macAddr
      const type = item.type // 'temperature' or 'humidity'
      
      if (!macAddr || !type) return
      
      // Initialize sensor data if not exists
      if (!sensorData[macAddr]) {
        sensorData[macAddr] = {
          temperatureEntries: [],
          humidityEntries: []
        }
      }
      
      // Add entries based on type
      if (type === 'temperature') {
        sensorData[macAddr].temperatureEntries.push({
          min: parseFloat(item.min),
          max: parseFloat(item.max),
          mean: parseFloat(item.mean),
          datetime: item.datetimezone
        })
      } else if (type === 'humidity') { // Note: API uses 'humidity' (typo)
        sensorData[macAddr].humidityEntries.push({
          min: parseFloat(item.min),
          max: parseFloat(item.max),
          mean: parseFloat(item.mean),
          datetime: item.datetimezone
        })
      }
    })
    
    console.log('sensorData:', sensorData)
    // Calculate overall statistics for each sensor from the pre-calculated entries
    const processedData = {}
    Object.keys(sensorData).forEach(macAddr => {
      const data = sensorData[macAddr]
      
      // Temperature statistics from all temperature entries
      if (data.temperatureEntries.length > 0) {
        const tempEntries = data.temperatureEntries
        const allMins = tempEntries.map(e => e.min).filter(v => !isNaN(v) && v !== null && v !== undefined)
        const allMaxs = tempEntries.map(e => e.max).filter(v => !isNaN(v) && v !== null && v !== undefined)
        const allMeans = tempEntries.map(e => e.mean).filter(v => !isNaN(v) && v !== null && v !== undefined)
        
        if (allMins.length > 0 && allMaxs.length > 0 && allMeans.length > 0) {
          processedData[macAddr] = {
            temperatureStats: {
              min: Math.min(...allMins),
              max: Math.max(...allMaxs),
              avg: allMeans.reduce((a, b) => a + b, 0) / allMeans.length,
              count: tempEntries.length
            }
          }
        }
      }
      
      // Humidity statistics from all humidity entries
      if (data.humidityEntries.length > 0) {
        const humEntries = data.humidityEntries
        const allMins = humEntries.map(e => e.min).filter(v => !isNaN(v) && v !== null && v !== undefined)
        const allMaxs = humEntries.map(e => e.max).filter(v => !isNaN(v) && v !== null && v !== undefined)
        const allMeans = humEntries.map(e => e.mean).filter(v => !isNaN(v) && v !== null && v !== undefined)
        
        if (allMins.length > 0 && allMaxs.length > 0 && allMeans.length > 0) {
          if (!processedData[macAddr]) processedData[macAddr] = {}
          processedData[macAddr].humidityStats = {
            min: Math.min(...allMins),
            max: Math.max(...allMaxs),
            avg: allMeans.reduce((a, b) => a + b, 0) / allMeans.length,
            count: humEntries.length
          }
        }
      }
    })
    
    console.log('✅ Données historiques traitées avec min/max pré-calculés:', processedData)
    return processedData
  }

  // Refresh sensors data
  const handleRefresh = () => {
    console.log('🔄 Actualisation des capteurs...')
    handleLoadSensors()
  }

  // Transform backend engin data to sensor format
  const transformSensorsData = (enginsData) => {
    if (!enginsData || !Array.isArray(enginsData)) return []
    
    return enginsData.map(engin => ({
      // Keep all original backend fields
      ...engin,
      // Add computed fields for display based on new engin format
      id: engin.uid || engin.id,
      label: engin.title || engin.label || engin.reference || `Engin-${engin.uid}`,
      macAddr: engin.tagname || engin.macAddr || engin.uid || 'N/A',
      name: engin.title || engin.label,
      actif: engin.sysActive === 1,
      status: engin.sysActive === 1 ? 'Actif' : 'Inactif',
      temperature: engin.temperature !== null ? parseFloat(engin.temperature) || 0 : 0,
      humidity: engin.humidity !== null ? parseFloat(engin.humidity) || 0 : 0,
      battery: parseInt(engin.batteries),
      rssi: engin.rssi || engin.signal || 0,
      lastSeen: engin.lastSeenAt || engin.tagDate || new Date().toISOString(),
      isOnline: engin.sysActive === 1,
      batteryLevel: parseInt(engin.batteries) || 0,
      // Additional engin-specific fields
      statuslabel: engin.statuslabel,
      statusname: engin.statusname,
      statusbgColor: engin.statusbgColor,
      etatengin: engin.etatengin,
      etatenginname: engin.etatenginname,
      LocationObject: engin.LocationObject,
      LocationObjectname: engin.LocationObjectname,
      familleTag: engin.familleTag,
      cost: engin.cost,
      currency: engin.currency
    }))
  }

  // Process sensors data - no mock data fallback
  const allSensors = React.useMemo(() => {
    return transformSensorsData(sensors)
  }, [sensors])

  // Group sensors by the selected groupBy field
  const groupedSensors = React.useMemo(() => {
    if (!groupBy) return allSensors

    const groups = allSensors.reduce((acc, sensor) => {
      const groupValue = sensor[groupBy.field] || 'Non défini'
      if (!acc[groupValue]) {
        acc[groupValue] = []
      }
      acc[groupValue].push(sensor)
      return acc
    }, {})

    // Convert to flat array with group headers
    const result = []
    Object.entries(groups).forEach(([groupName, sensors]) => {
      // Add group header
      result.push({
        isGroupHeader: true,
        groupName,
        groupCount: sensors.length,
        id: `group-${groupName}`
      })
      // Add sensors in this group
      result.push(...sensors)
    })

    return result
  }, [allSensors, groupBy])

  // Filter sensors based on global filter for grid view
  const filteredSensors = React.useMemo(() => {
    if (!globalFilterValue) return allSensors
    
    const searchTerm = globalFilterValue.toLowerCase()
    return allSensors.filter(sensor => 
      sensor.label?.toLowerCase().includes(searchTerm) ||
      sensor.macAddr?.toLowerCase().includes(searchTerm) ||
      sensor.status?.toLowerCase().includes(searchTerm) ||
      sensor.statuslabel?.toLowerCase().includes(searchTerm) ||
      sensor.reference?.toLowerCase().includes(searchTerm)
    )
  }, [allSensors, globalFilterValue])

  const onNew = () => {
    dispatch(setSelectedEngine(null))
    dispatch(setEditEngine(true))
  }

  const handleViewDetail = (sensor) => {
    setSelectedSensorForDetail(sensor)
    setShowDetail(true)
  }

 

  // Template functions for columns
  const photoBodyTemplate = (rowData) => {
    if (rowData.isGroupHeader) return null
    
    const imageSrc = rowData?.image ? `${API_BASE_URL_IMAGE}${rowData.image}` : null
    
    if (!imageSrc) {
      return (
        <div 
          className='flex align-items-center justify-content-center rounded bg-gray-200'
          style={{width: '40px', height: '40px'}}
        >
          <i className="fas fa-microchip text-gray-500" style={{fontSize: '16px'}}></i>
        </div>
      )
    }
    
    return (
      <img
        src={imageSrc}
        alt='Sensor'
        width='40'
        height='40'
        className='border-round'
        style={{objectFit: 'cover'}}
        onError={(e) => {
          e.target.style.display = 'none'
          e.target.parentElement.innerHTML = `
            <div class="flex align-items-center justify-content-center rounded bg-gray-200" style="width: 40px; height: 40px;">
              <i class="fas fa-microchip text-gray-500" style="font-size: 16px;"></i>
            </div>
          `
        }}
      />
    )
  }


  const statusBodyTemplate = (rowData) => {
    const severity = rowData.actif ? 'success' : 'danger'
    const label = rowData.actif ? 'Actif' : 'Inactif'
    return <Badge value={label} severity={severity} />
  }

  const batteryBodyTemplate = (rowData) => {
    const getBatteryColor = (battery) => {
      if (battery > 60) return 'text-green-600'
      if (battery > 30) return 'text-orange-500'
      return 'text-red-500'
    }
    const batteryColor = getBatteryColor(rowData.battery)
    return (
      <div className="flex align-items-center gap-1">
        <span className="text-xs" style={{ color: batteryColor }}>{isNaN(rowData.battery) ? '_' : `${rowData.battery}%`}</span>
      </div>
    )
  }

  

  const latestValuesBodyTemplate = (rowData) => {
    if (rowData.isGroupHeader) return ''
    
    const tempValue = rowData.temperature !== null && rowData.temperature !== undefined ? rowData.temperature : null
    const humidityValue = rowData.humidity !== null && rowData.humidity !== undefined ? rowData.humidity : null
    
    return (
      <div className="flex flex-column gap-1" style={{ padding: '4px' }}>
        {/* Température */}
        <div className="flex align-items-center gap-2">
          <i className="fa-regular fa-temperature-full text-red-500" style={{ fontSize: '12px' }}></i>
          <span className="text-red-600 font-semibold" style={{ fontSize: '14px' }}>
            {tempValue !== null ? `${tempValue}°C` : '-'}
          </span>
        </div>
        {/* Humidité */}
        <div className="flex align-items-center gap-2">
          <i className="fa-duotone fa-regular fa-droplet-degree text-blue-500" style={{ fontSize: '12px' }}></i>
          <span className="text-blue-600 font-semibold" style={{ fontSize: '14px' }}>
            {humidityValue !== null ? `${humidityValue}%` : '-'}
          </span>
        </div>
      </div>
    )
  }

  const signalBodyTemplate = (rowData) => {
    if (rowData.isGroupHeader) return null
    
    const signalValue = rowData.signal
    if (signalValue === null || signalValue === undefined) return '-'
    
    const signalStrength = signalValue > -50 ? 'excellent' : 
                          signalValue > -70 ? 'good' : 
                          signalValue > -85 ? 'fair' : 'poor'
    
    const color = signalStrength === 'excellent' ? '#4CAF50' : 
                  signalStrength === 'good' ? '#8BC34A' : 
                  signalStrength === 'fair' ? '#FF9800' : '#F44336'
    
    return (
      <div className="flex align-items-center gap-1">
        <i className="pi pi-wifi text-xs" style={{ color }}></i>
        <span className="text-xs">{signalValue}</span>
      </div>
    )
  }



  const lastSeenBodyTemplate = (rowData) => {
    if (rowData.isGroupHeader) return null
    if(!rowData.lastSeenAt ) return <span className='text-gray-500'>-</span>
    
    const lastSeen = new Date(rowData.lastSeen || rowData.lastSeenAt)
    const now = new Date()
    const diffMs = now.getTime() - lastSeen.getTime()
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    const diffMonths = Math.floor(diffDays / 30)
    const restDays = diffDays % 30

    let timeText = ''
    let colorClass = ''

    if (diffMinutes < 5) {
      timeText = "À l'instant"
      colorClass = 'text-green-600'
    } else if (diffMinutes < 60) {
      timeText = `Il y a ${diffMinutes} min`
      colorClass = diffMinutes < 30 ? 'text-blue-600' : 'text-orange-500'
    } else if (diffHours < 24) {
      timeText = `Il y a ${diffHours}h`
      colorClass = 'text-red-500'
    } else if (diffDays < 30) {
      timeText = `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`
      colorClass = 'text-red-500'
    } else {
      timeText =
        `Il y a ${diffMonths} mois` +
        (restDays > 0 ? ` ${restDays} jour${restDays > 1 ? 's' : ''}` : '')
      colorClass = 'text-red-500'
    }

    return <span className={colorClass}>{timeText}</span>
  }

  const featuresBodyTemplate = (rowData) => {
    if (rowData.isGroupHeader) return null
    
    return (
      <div className='flex gap-1'>
        {rowData.rapports && (
          <Chip label='Rapports' className='text-xs bg-blue-100 text-blue-800' />
        )}
        {rowData.alertes && (
          <Chip label='Alertes' className='text-xs bg-orange-100 text-orange-800' />
        )}
      </div>
    )
  }

  const doorStateBodyTemplate = (rowData) => {
    if (rowData.isGroupHeader) return null
    
    return rowData.ouvert ? (
      <span className='text-red-600 font-bold'>Ouvert</span>
    ) : (
      <span className='text-green-600 font-bold'>Fermé</span>
    )
  }

  // Handle edit sensor
  const handleEditSensor = (rowData) => {
    console.log('✏️ Édition du capteur:', rowData)
    dispatch(fetchValidator('engin'))
    dispatch(setSelectedEngine(rowData))
    dispatch(setEditEngine(true))
  }

  // Handle delete sensor
  const handleDeleteSensor = (rowData) => {
    console.log('🗑️ Suppression du capteur:', rowData)
    dispatch(setSelectedEngine(rowData))
    dispatch(
      setAlertParams({
        title: 'Supprimer',
        message: 'Voulez-vous vraiment supprimer ce capteur?',
        acceptClassName: 'p-button-danger',
        visible: true,
        accept: () => {
          dispatch(removeEngine({ data: rowData, page: 0, pageSize: 1000 })).then(() => {
            toast.current?.show({
              severity: 'success',
              summary: 'Succès',
              detail: 'Capteur supprimé avec succès',
              life: 3000
            })
            // Recharger la liste
            handleLoadSensors()
          })
        },
      })
    )
  }

  const header = (
    <div className="flex flex-wrap gap-2 align-items-center justify-content-between">
      <div className="flex align-items-center gap-3">
        {/* <h2 className="text-2xl font-bold text-900 m-0"><OlangItem olang='sensor.list' /></h2> */}
        <Badge value={sensors.length} className="ml-2" />
        
        {/* Sélecteur de période */}
        <div className="flex align-items-center gap-2 ml-4">
          <label className="font-semibold text-700"><OlangItem olang='period' />:</label>
          <Dropdown
            value={selectedPeriod}
            options={periodOptions}
            onChange={(e) => setSelectedPeriod(e.value)}
            placeholder="Sélectionner une période"
            className="w-10rem"
            disabled={loadingHistories}
          />
         
        </div>
        <div className="flex align-items-center gap-2 ml-4">
          <label className="font-semibold text-700"><OlangItem olang='zone' />:</label>
          <Dropdown
            value={selectedWorksite}
            options={[{label: 'Tout' , value: 0}, ...worksites]}
            onChange={(e) => setSelectedWorksite(e.value)}
            placeholder="Sélectionner une zone"
            className="w-10rem"
            disabled={loadingHistories}
          />
          {loadingHistories && (
            <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="4" />
          )}
        </div>
      </div>
    </div>
  )

  if (showDetail && selectedSensorForDetail) {
    return (
      <SensorDetail
        sensor={selectedSensorForDetail}
        onBack={() => {
          setShowDetail(false)
          setSelectedSensorForDetail(null)
        }}
      />
    )
  }

  if (showAlerts) {
    return (
      <AlertsPage
        alerts={[]}
        onBack={() => setShowAlerts(false)}
      />
    )
  }

  // Loading state with professional spinner
  if (loading) {
    return (
      <Card className="m-3">
        <div className="flex flex-column align-items-center justify-content-center p-4" style={{ minHeight: '400px' }}>
          <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
          <h3 className="mt-3 text-600">Chargement des capteurs</h3>
          <p className="mt-3">
            Chargement de la liste...
          </p>
          <div>
          <span class="loading loading-bars loading-xs"></span>
<span class="loading loading-bars loading-sm"></span>
<span class="loading loading-bars"></span>
<span class="loading loading-bars loading-lg"></span>
<span class="loading loading-bars loading-xl"></span>
          </div>
        </div>
      </Card>
    )
  }

  // Error state
  if (error) {
    return (
      <Card className="m-3">
        <div className="flex flex-column align-items-center justify-content-center p-4">
          <i className="pi pi-exclamation-triangle text-red-500" style={{ fontSize: '3rem' }}></i>
          <h3 className="text-red-500 mt-3">Erreur</h3>
          <p className="text-center">{error}</p>
          <Button 
            label="Réessayer" 
            icon="pi pi-refresh"
            onClick={handleRefresh}
            className="mt-3"
          />
        </div>
      </Card>
    )
  }

  // No data state
  if (/*sensors.length === 0 ||*/ !1) {
    return (
      <Card className="m-3">
        <div className="flex flex-column align-items-center justify-content-center p-4">
          <i className="pi pi-info-circle text-blue-500" style={{ fontSize: '3rem' }}></i>
          <h3 className="text-blue-500 mt-3">Aucun capteur</h3>
          <p className="text-center">
            Aucun capteur n'a été trouvé.
          </p>
          <Button 
            label="Actualiser" 
            icon="pi pi-refresh"
            onClick={handleRefresh}
            className="mt-3 p-button-outlined"
          />
        </div>
      </Card>
    )
  }

  // Grid view component
  const renderGridView = () => {
    return (
      <div className="grid">
        {filteredSensors.map((sensor) => {
          const macAddr = sensor.tagname || sensor.macAddr
          const tempStats = historicalData[macAddr]?.temperatureStats
          const humStats = historicalData[macAddr]?.humidityStats
          
          return (
            <div key={sensor.id} className="col-12 md:col-6 lg:col-4 xl:col-3">
              <Card className="shadow-2 hover:shadow-4 transition-all transition-duration-300 flex flex-column" style={{ minHeight: '420px', height: '100%' }}>
                {/* Header avec photo, nom et statut */}
                <div className="flex align-items-center justify-content-between mb-3 pb-3 border-bottom-1 border-200">
                  <div className="flex align-items-center gap-3 flex-1">
                    {/* Photo du capteur */}
                    <div className="flex-shrink-0">
                      {photoBodyTemplate(sensor)}
                    </div>
                    <div className="flex-1">
                      <h3 className="m-0 text-lg font-bold text-900 mb-1" style={{ 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap' 
                      }} title={sensor.label}>
                        {sensor.label}
                      </h3>
                      <span className="text-xs font-mono text-600">{sensor.macAddr}</span>
                    </div>
                  </div>
                  <div>
                    {statusBodyTemplate(sensor)}
                  </div>
                </div>

                {/* Statistiques temp/humidité */}
                <div className="grid mb-3">
                  <div className="col-6">
                    <div className="text-center p-3 border-round bg-red-50">
                      <div className="flex align-items-center justify-content-center gap-1 mb-2">
                        <i className="fa-regular fa-temperature-full text-red-600"></i>
                        <span className="text-xs font-semibold text-red-900">Température</span>
                      </div>
                      {tempStats && tempStats.count > 0 ? (
                        <>
                          <div className="text-2xl font-bold text-red-600 mb-1">
                            {tempStats.avg.toFixed(1)}°C
                          </div>
                          <div className="text-xs text-red-500">
                            <div>Max: {tempStats.max.toFixed(1)}°C</div>
                            <div>Min: {tempStats.min.toFixed(1)}°C</div>
                          </div>
                        </>
                      ) : (
                        <div className="text-xl font-bold text-red-600">
                          {sensor.temperature !== null ? `${sensor.temperature}°C` : '-'}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-6">
                    <div className="text-center p-3 border-round bg-blue-50">
                      <div className="flex align-items-center justify-content-center gap-1 mb-2">
                        <i className="fa-duotone fa-regular fa-droplet-degree text-blue-600"></i>
                        <span className="text-xs font-semibold text-blue-900">Humidité</span>
                      </div>
                      {humStats && humStats.count > 0 ? (
                        <>
                          <div className="text-2xl font-bold text-blue-600 mb-1">
                            {humStats.avg.toFixed(1)}%
                          </div>
                          <div className="text-xs text-blue-500">
                            <div>Max: {humStats.max.toFixed(1)}%</div>
                            <div>Min: {humStats.min.toFixed(1)}%</div>
                          </div>
                        </>
                      ) : (
                        <div className="text-xl font-bold text-blue-600">
                          {sensor.humidity !== null ? `${sensor.humidity}%` : '-'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Dernières valeurs reçues */}
                <div className="mb-3">
                  <div className="text-center p-3 border-round bg-gray-50">
                    <div className="flex align-items-center justify-content-center gap-1 mb-2">
                      <i className="pi pi-clock text-gray-600"></i>
                      <span className="text-xs font-semibold text-gray-900">Dernières Valeurs</span>
                    </div>
                    <div className="flex align-items-center justify-content-center gap-4">
                      {/* Température actuelle */}
                      <div className="flex align-items-center gap-2">
                        <i className="fa-regular fa-temperature-full text-red-500" style={{ fontSize: '14px' }}></i>
                        <span className="text-red-600 font-bold" style={{ fontSize: '16px' }}>
                          {sensor.temperature !== null && sensor.temperature !== undefined ? `${sensor.temperature}°C` : '-'}
                        </span>
                      </div>
                      {/* Humidité actuelle */}
                      <div className="flex align-items-center gap-2">
                        <i className="fa-duotone fa-regular fa-droplet-degree text-blue-500" style={{ fontSize: '14px' }}></i>
                        <span className="text-blue-600 font-bold" style={{ fontSize: '16px' }}>
                          {sensor.humidity !== null && sensor.humidity !== undefined ? `${sensor.humidity}%` : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Informations supplémentaires */}
                <div className="grid mb-3">
                  <div className="col-6">
                    <div className="flex align-items-center gap-2">
                      <i className="pi pi-battery-full text-green-600"></i>
                      <span className="text-sm">{sensor.battery}%</span>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="flex align-items-center gap-2">
                      {signalBodyTemplate(sensor)}
                    </div>
                  </div>
                </div>

                {/* Dernière activité */}
                <div className="mb-3 p-2 border-round bg-gray-50">
                  <div className="flex align-items-center gap-2 text-xs">
                    <i className="pi pi-clock text-600"></i>
                    <span className="text-600">Vu:</span>
                    {lastSeenBodyTemplate(sensor)}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    icon="pi pi-eye"
                    className="flex-1 p-button-sm p-button-outlined"
                    onClick={() => handleViewDetail(sensor)}
                    tooltip="Voir les détails"
                  />
                  <Button
                    icon="pi pi-pencil"
                    className="p-button-sm p-button-outlined p-button-info"
                    onClick={() => handleEditSensor(sensor)}
                    tooltip="Modifier"
                  />
                  <Button
                    icon="pi pi-trash"
                    className="p-button-sm p-button-outlined p-button-danger"
                    onClick={() => handleDeleteSensor(sensor)}
                    tooltip="Supprimer"
                  />
                </div>
              </Card>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="sensor-list-container">
      <Toast ref={toast} />
      <style>{groupHeaderStyles}</style>
      
      {/* Page Header avec bouton retour, titre et info */}
      <PageHeader
        titleOlang="sensor.list"
        backPath="/dashboard"
        info={
          <div className="flex align-items-center gap-3">
            <SelectButton
                value={viewMode}
                onChange={(e) => setViewMode(e.value)}
                options={viewOptions}
                optionLabel='label'
                optionValue='value'
                className='p-selectbutton-sm'
              />
              <Divider layout='vertical' />
            <div className="flex align-items-center gap-2">
              <i className="pi pi-wifi text-xl text-primary"></i>
              <span className="text-lg font-semibold">{filteredSensors.length} capteur{filteredSensors.length > 1 ? 's' : ''}</span>
            </div>
            {activeAlertsCount > 0 && (
              <>

                <div className="border-left-2 border-300" style={{ height: '30px' }}></div>
                <div className="flex align-items-center gap-2">
                  <i className="pi pi-exclamation-triangle text-xl text-red-500"></i>
                  <span className="text-lg font-semibold text-red-600">{activeAlertsCount} alerte{activeAlertsCount > 1 ? 's' : ''}</span>
                </div>
              </>
            )}
          </div>
        }
        className="m-3"
      />
      
      {/* Badge des alertes actives en haut de la page */}
      <div className="hidden justify-content-center mb-3">
        <Button
          icon="pi pi-bell"
          label={`${activeAlertsCount} alerte(s) active(s)`}
          className="p-button-warning p-button-lg"
          badge={activeAlertsCount > 0 ? String(activeAlertsCount) : null}
          badgeClassName="p-badge-danger"
          onClick={() => setShowAlerts(true)}
          style={{ fontSize: '1.1rem', padding: '0.75rem 1.5rem' }}
        />
      </div>
      
      <Card>
        {/* Header commun aux deux vues */}
        {/* {header} */}
        
        {/* Vue conditionnelle : grille ou liste */}
        {viewMode === 'grid' ? (
          <div className="mt-4">
            {filteredSensors.length === 0 ? (
              <div className="text-center py-8">
                <i className="pi pi-search text-400 text-6xl mb-3"></i>
                <p className="text-600">Aucun capteur trouvé</p>
              </div>
            ) : (
              renderGridView()
            )}
          </div>
        ) : (
          <DataTableComponent 
             tableId={"sensor-list"}
             
             data={groupedSensors}
             columns={columns}
             rowActions={actions}
             tableTitle={header}
             tableOptions = {{ showGridlines: false }}
             extraActionTemplate={<div>
                     <Button
                      icon="pi pi-bell"
                      label={`${activeAlertsCount} alerte(s) active(s)`}
                      className="p-button-warning p-button-lg"
                      badge={activeAlertsCount > 0 ? String(activeAlertsCount) : null}
                      badgeClassName="p-badge-danger"
                      onClick={() => setShowAlerts(true)}
                      style={{ fontSize: '1.1rem', padding: '0.75rem 1.5rem' }}
                      size='small'
                    />
                      
             </div>}
             onNew={onNew}
          />
        )}
      </Card>
      {/* Modal pour ajouter/éditer un capteur */}
      <EnginEditor 
        onHide={() => {
          dispatch(setEditEngine(false))
          // Recharger la liste des capteurs après la sauvegarde
          handleLoadSensors()
        }}
      />
      
      {/* Toast pour les notifications */}
      <Toast ref={toast} />
    </div>
  )
}

export default SensorList
